import React from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getGainLossColor } from "../../../../../theme";

export interface GrowthMetric {
  /** Label for the metric (e.g., "YoY Growth", "5Y CAGR") */
  label: string;
  /** The percentage value */
  value: number;
  /** Optional: use stacked layout (label above badge) */
  stacked?: boolean;
}

export interface GrowthMetricsBarProps {
  /** Array of growth metrics to display */
  metrics: GrowthMetric[];
  /** Whether to show the metrics bar */
  show?: boolean;
}

/**
 * Growth metric badge component
 */
const GrowthBadge: React.FC<{ metric: GrowthMetric }> = ({ metric }) => {
  const isPositive = metric.value >= 0;

  const badge = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        backgroundColor: isPositive
          ? "rgba(34, 197, 94, 0.1)"
          : "rgba(239, 68, 68, 0.1)",
        color: getGainLossColor(metric.value),
        paddingX: 1,
        paddingY: 0.25,
        borderRadius: 1,
        minWidth: "60px",
        justifyContent: "center",
        border: `1px solid ${
          isPositive ? "rgba(34, 197, 94, 0.3)" : "rgba(239, 68, 68, 0.3)"
        }`,
      }}
    >
      <Typography variant="caption" fontWeight={600}>
        {isPositive ? "+" : ""}
        {metric.value.toFixed(1)}%
      </Typography>
    </Box>
  );

  if (metric.stacked) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0.5,
          minHeight: "48px",
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.75rem", textAlign: "center" }}
        >
          {metric.label}
        </Typography>
        {badge}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        minHeight: "32px",
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontSize: "0.75rem" }}
      >
        {metric.label}
      </Typography>
      {badge}
    </Box>
  );
};

/**
 * Generic growth metrics bar for financial charts.
 * Displays growth percentages with trending icons.
 */
export const GrowthMetricsBar: React.FC<GrowthMetricsBarProps> = ({
  metrics,
  show = true,
}) => {
  const theme = useTheme();

  if (!show || metrics.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: 1.5,
        mt: 0.5,
        pt: 1,
        pb: 0.5,
        borderTop: `1px solid ${theme.palette.divider}`,
        flexWrap: "wrap",
        minHeight: "58px",
      }}
    >
      {metrics.map((metric, index) => (
        <GrowthBadge key={`${metric.label}-${index}`} metric={metric} />
      ))}
    </Box>
  );
};

export default GrowthMetricsBar;
